import * as React from 'react';
import { useSelector } from 'react-redux';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { AttachmentContext } from './AttachmentForm';

function Info() {
  const formData = useSelector((state) => state.projects.formData);
  const activeStep = useSelector((state) => state.projects.activeStep);
  const programName = useSelector((state) => state.projects.programName);
  const ownerName = useSelector((state) => state.projects.ownerName);
  const locationName = useSelector((state) => state.projects.locationName);
  const existingAttachments = useSelector((state) => state.projects.existingAttachments);
  
  // Get attachment files with descriptions from context
  const { files: attachmentFiles } = React.useContext(AttachmentContext);
  
  // Build dynamic summary based on completed steps
  const getSummaryItems = () => {
    const items = [];

    // Step 1: Project Info (show after step 0 is completed, i.e., when activeStep >= 1)
    if (activeStep >= 1) {
      items.push(
        {
          label: 'Project Title',
          value: formData.title || '-',
          completed: true,
        },
        {
          label: 'Project Code',
          value: formData.projectCode || '-',
          completed: true,
        },
        {
          label: 'Program',
          value: programName || '-',
          completed: true,
        },
        {
          label: 'Owner',
          value: ownerName || '-',
          completed: true,
        }
      );

      if (locationName) {
        items.push({
          label: 'Location',
          value: locationName,
          completed: true,
        });
      }

      if (formData.description) {
        items.push({
          label: 'Description',
          value: formData.description.length > 50 
            ? formData.description.substring(0, 50) + '...'
            : formData.description,
          completed: true,
        });
      }

      if (formData.projectFilePath) {
        items.push({
          label: 'File Path',
          value: formData.projectFilePath,
          completed: true,
        });
      }
    }

    // Step 2: Deliverable Types (show after step 1 is completed, i.e., when activeStep >= 2)
    if (activeStep >= 2) {
      const selectedDeliverableTypes = formData.selectedDeliverableTypes || [];
      if (selectedDeliverableTypes.length > 0) {
        items.push({
          label: `Deliverable Types (${selectedDeliverableTypes.length} selected)`,
          value: `${selectedDeliverableTypes.length} type${selectedDeliverableTypes.length !== 1 ? 's' : ''} assigned`,
          completed: true,
        });
      } else {
        items.push({
          label: 'Deliverable Types',
          value: 'None selected',
          completed: true,
        });
      }
    }

    // Step 3: Attachments (show after step 2 is completed, i.e., when activeStep >= 3)
    if (activeStep >= 3) {
      const attachmentNames = formData.attachmentNames || [];
      const existingCount = existingAttachments?.length || 0;
      const totalCount = existingCount + attachmentNames.length;
      
      if (totalCount > 0) {
        items.push({
          label: 'Attachments',
          value: `${totalCount} file${totalCount !== 1 ? 's' : ''} (${existingCount} existing, ${attachmentNames.length} new)`,
          completed: true,
          isAttachmentSection: true, // Flag to render custom section
        });
      } else {
        items.push({
          label: 'Attachments',
          value: 'No files attached',
          completed: true,
        });
      }
    }

    return items;
  };

  const summaryItems = getSummaryItems();

  // Step status indicators
  const steps = [
    { name: 'Project Info', completed: activeStep >= 1 },
    { name: 'Deliverable Types', completed: activeStep >= 2 },
    { name: 'Attachments', completed: activeStep >= 3 },
    { name: 'Review', completed: activeStep >= 4 },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
        Progress
      </Typography>

      {/* Step Indicators */}
      <List disablePadding sx={{ mb: 2 }}>
        {steps.map((step, index) => (
          <ListItem key={step.name} sx={{ py: 0.5, px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {step.completed ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
              ) : (
                <CircleOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: step.completed ? 'text.primary' : 'text.disabled',
                  fontWeight: step.completed ? 500 : 400
                }}
              >
                {step.name}
              </Typography>
            </Box>
          </ListItem>
        ))}
      </List>

      {summaryItems.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Summary
          </Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            <List disablePadding>
              {summaryItems.map((item) => (
                <React.Fragment key={item.label}>
                  <ListItem sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      sx={{ m: 0 }}
                      primary={item.label}
                      secondary={item.value}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          sx: { fontWeight: 500, color: 'text.primary', mb: 0.25 }
                        },
                        secondary: {
                          variant: 'body2',
                          sx: { 
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }
                        }
                      }}
                    />
                  </ListItem>
                  {/* Show detailed attachment list if this is the attachment section */}
                  {item.isAttachmentSection && (
                    <Box sx={{ pl: 0, pb: 1 }}>
                      {/* Existing Attachments */}
                      {existingAttachments && existingAttachments.length > 0 && existingAttachments.map((attachment, idx) => (
                        <Box
                          key={`existing-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.75,
                            px: 1,
                            mb: 0.5,
                            backgroundColor: 'success.lighter',
                            borderRadius: 0.5,
                            border: 1,
                            borderColor: 'success.light',
                          }}
                        >
                          <InsertDriveFileIcon sx={{ fontSize: 16, color: 'success.main', mt: 0.25 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'success.dark',
                              }}
                            >
                              {attachment.name}
                            </Typography>
                            {attachment.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'success.dark',
                                  display: 'block',
                                  mt: 0.25,
                                  fontSize: '0.7rem',
                                  opacity: 0.8,
                                }}
                              >
                                {attachment.description}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'success.dark',
                                display: 'block',
                                mt: 0.25,
                                fontSize: '0.65rem',
                                fontStyle: 'italic',
                              }}
                            >
                              Existing
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      
                      {/* New Attachments */}
                      {attachmentFiles && attachmentFiles.length > 0 && attachmentFiles.map((fileObj, idx) => (
                        <Box
                          key={`new-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            py: 0.75,
                            px: 1,
                            mb: 0.5,
                            backgroundColor: 'action.hover',
                            borderRadius: 0.5,
                          }}
                        >
                          <InsertDriveFileIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fileObj.file.name}
                            </Typography>
                            {fileObj.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                  mt: 0.25,
                                  fontSize: '0.7rem',
                                }}
                              >
                                {fileObj.description}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                mt: 0.25,
                                fontSize: '0.65rem',
                                fontStyle: 'italic',
                              }}
                            >
                              New
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </>
      )}
      {summaryItems.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 2 }}>
          Complete the form to see your project summary here.
        </Typography>
      )}
    </Box>
  );
}

export default Info;
